import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { openContact } from "./ContactDrawer";

const LINKS = [
  { id: "home", label: "Home", href: "#top" },
  { id: "webdesign", label: "Webdesign", href: "#webdesign" },
  { id: "clothing-design", label: "Clothing Design", href: "#clothing-design" },
  { id: "clothing-brand", label: "Clothing Brand", href: "#clothing-brand" },
  { id: "portfolio", label: "Portfolio", href: "#portfolio" },
  { id: "ueber-mich", label: "Über mich", href: "#ueber-mich" },
  { id: "partner", label: "Partner", href: "#partner" },
];

export function scrollToSection(href) {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export function requestService(key) {
  trackEvent("auftrag_gestartet");
  window.dispatchEvent(new CustomEvent("jh:service", { detail: key }));
  openContact(key);
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#0a0a0a]/75 border-b border-white/10"
            : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 h-20 flex items-center justify-between">
          <a
            href="#top"
            onClick={go("#top")}
            data-testid="header-logo"
            className="font-display font-bold uppercase tracking-tight text-lg leading-none transition-colors duration-500 text-paper"
          >
            Justin<span className="text-gold">.</span>
            <span className={`block text-[9px] font-body font-normal tracking-[0.3em] mt-1 transition-colors duration-500 ${scrolled ? "text-zinc-500" : "text-zinc-400"}`}>
              Web Design &amp; Clothing
            </span>
          </a>
          <nav className="hidden lg:flex items-center gap-8" data-testid="header-nav">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={go(l.href)}
                data-testid={`nav-link-${l.id}`}
                className="text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 text-zinc-300 hover:text-paper"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <a
              href="#kontakt"
              onClick={(e) => {
                e.preventDefault();
                openContact();
              }}
              data-testid="header-cta-button"
              className="hidden sm:inline-flex group relative overflow-hidden px-6 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors duration-500 bg-paper text-ink"
            >
              <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              <span className="relative group-hover:text-ink transition-colors duration-500">
                Projekt starten
              </span>
            </a>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-button"
              aria-label="Menü öffnen"
              className="lg:hidden p-2 -mr-2 transition-colors duration-500 text-paper"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-[95] bg-[#0a0a0a] text-bone flex flex-col"
          >
            <div className="h-20 px-6 flex items-center justify-between">
              <span className="font-display font-bold uppercase tracking-tight text-lg leading-none">
                Justin<span className="text-gold">.</span>
                <span className="block text-[9px] font-body font-normal tracking-[0.3em] mt-1 text-zinc-500">
                  Web Design &amp; Clothing
                </span>
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                aria-label="Menü schließen"
                className="p-2 -mr-2 text-bone"
              >
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  onClick={go(l.href)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="font-display uppercase tracking-tight text-4xl py-3 border-b border-white/10 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
